import { useEffect, useState, type ReactNode } from "react";
// useState is used by BounceZonesEditor and ZoneRow below
import { Tooltip } from "@/components/Tooltip";
import { useApp, type TradingMode, type UserBounceZone } from "@/context/AppContext";
import { formatUsdCompact, formatSol } from "@/lib/formatUsd";
import {
  isBotTradeChain,
  type BotTradeRow,
  type BotTradeRowTape,
  type ScalperPaperSnapshot,
} from "@/lib/scalperPaperEngine";
import { SetupPanel } from "@/components/SetupPanel";
import { WorkspacePanel } from "@/components/WorkspacePanel";
import { PerformancePanel } from "@/components/PerformancePanel";
import { TrainingDataPanel } from "@/components/TrainingDataPanel";
import { NurseryPanel } from "@/components/NurseryPanel";
import { WalletTab } from "@/components/WalletTab";
import { AgentCallouts } from "@/components/AgentCallouts";
import { Sprout, Wallet, Flame, ChevronDown, ChevronUp, BrainCircuit } from "lucide-react";
import { SvixTradingPanel } from "@/components/SvixTradingPanel";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { AlertsPanel } from "@/components/AlertsPanel";
import { PointsPanel } from "@/components/PointsPanel";
import { GlobalDeploymentToasts } from "@/components/GlobalDeploymentToasts";
import { P2pEscrowPanel } from "@/components/P2pEscrowPanel";
import FlyBrainVisualizer from "@/components/FlyBrainVisualizer";
// import { FomoLeaderboard } from "@/components/FomoLeaderboard";

export function DashboardSidebar() {
  const { sidebarMode, setSidebarMode, setActivitySection, setCaMintInput } = useApp() as any;
  const { t } = useTranslation();

  const handleTab = (mode: string) => {
    setSidebarMode(mode);
    setActivitySection(mode);
  };

  return (
    <div className="flex flex-col h-full w-full bg-[var(--color-bg-panel)] relative overflow-hidden">
      <GlobalDeploymentToasts />
      {/* Sleek top navigation header for desktop/sidebar */}
      <div className="flex items-center w-full border-b border-white/5 bg-transparent p-2 shrink-0 gap-1.5 z-20 overflow-x-auto no-scrollbar">
        <button
          onClick={() => handleTab("nursery")}
          className={`flex-1 py-1.5 px-3 text-xs font-semibold rounded-md transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
            sidebarMode === "nursery" ? "bg-emerald-500/10 text-emerald-400" : "text-white/40 hover:text-white/80 hover:bg-white/5"
          }`}
          title="Nursery: Live launches & Lily quality gate"
        >
          <Sprout className="size-3.5" />
          <span>{t("nav.nursery")}</span>
        </button>

        <button
          onClick={() => handleTab("wallet")}
          className={`flex-1 py-1.5 px-3 text-xs font-semibold rounded-md transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
            sidebarMode === "wallet" ? "bg-purple-500/10 text-purple-400" : "text-white/40 hover:text-white/80 hover:bg-white/5"
          }`}
          title="Phantom-like Solana Wallet"
        >
          <Wallet className="size-3.5" />
          <span>{t("nav.wallet")}</span>
        </button>

        <button
          onClick={() => handleTab("svix")}
          className={`flex-1 py-1.5 px-3 text-xs font-semibold rounded-md transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
            sidebarMode === "svix" ? "bg-red-500/10 text-red-400" : "text-white/40 hover:text-white/80 hover:bg-white/5"
          }`}
          title="SVIX Trading Interface"
        >
          <Flame className="size-3.5" />
          <span>{t("sidebar.svix")}</span>
        </button>

        <button
          onClick={() => handleTab("flybrain")}
          className={`flex-1 py-1.5 px-3 text-xs font-semibold rounded-md transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
            sidebarMode === "flybrain"
              ? "bg-green-500/10 text-green-400 shadow-[0_0_8px_rgba(0,255,100,0.2)]"
              : "text-white/40 hover:text-white/80 hover:bg-white/5"
          }`}
          title="FlyBrain: Live 3D Neural Connectome Engine"
        >
          <BrainCircuit className="size-3.5" />
          <span>FlyBrain</span>
        </button>
      </div>

      {/* Main panel content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {sidebarMode === "nursery" && <NurseryPanel onOpenToken={(m: string) => setCaMintInput(m)} />}

        {sidebarMode === "wallet" && <div className="p-2 h-full"><WalletTab viewMode="wallet" /></div>}
        {sidebarMode === "alerts" && <div className="h-full"><AlertsPanel /></div>}
        {sidebarMode === "points" && <div className="h-full"><PointsPanel /></div>}
        {sidebarMode === "analytics" && <SetupPanel />}
        {sidebarMode === "operations" && <AgentCallouts />}
        {sidebarMode === "code" && <WorkspacePanel />}
        {sidebarMode === "performance" && <div className="p-4"><PerformancePanel /></div>}
        {sidebarMode === "training" && <div className="p-4"><TrainingDataPanel /></div>}
        {sidebarMode === "svix" && <SvixTradingPanel />}
        {sidebarMode === "p2p" && <div className="h-full"><P2pEscrowPanel /></div>}
        {sidebarMode === "flybrain" && <div className="h-full p-3"><FlyBrainVisualizer /></div>}
      </div>
    </div>
  );
}



/** Titled section card for strategy settings. */
export function SettingsCard({ label, tip, accent, locked, liveSession, headerRight, collapsible, defaultCollapsed, children }: {
  label: string;
  tip: string;
  accent?: "sky";
  locked?: boolean;
  liveSession?: boolean;
  headerRight?: ReactNode;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed || false);
  const accentColor = accent === "sky" ? "#38bdf8" : "#2EA8FF";
  return (
    <div
      className="overflow-hidden rounded-lg border"
      style={{
        background: "var(--color-bg-editor)",
        borderColor: `color-mix(in srgb, ${accentColor} 18%, var(--color-border-subtle))`,
      }}
    >
      {/* Header strip */}
      <div
        className={`flex items-center gap-2 border-b px-3 py-2 ${collapsible ? 'cursor-pointer hover:bg-white/5 transition-colors' : ''}`}
        style={{
          borderColor: `color-mix(in srgb, ${accentColor} 15%, var(--color-border-subtle))`,
          background: `color-mix(in srgb, ${accentColor} 6%, transparent)`,
        }}
        onClick={() => collapsible && setCollapsed(!collapsed)}
      >
        <div
          className="h-3 w-0.5 shrink-0 rounded-full"
          style={{ background: `color-mix(in srgb, ${accentColor} 70%, transparent)` }}
        />
        <span className="text-[11px] font-semibold tracking-wide" style={{ color: `color-mix(in srgb, ${accentColor} 85%, var(--color-fg))` }}>
          {label}
        </span>
        <div onClick={(e) => e.stopPropagation()}>
          <HelpTip text={tip} />
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {locked ? (
            <span className="text-[9px] text-[var(--color-fg-dim)]">stop session to edit</span>
          ) : null}
          {liveSession ? (
            <span className="text-[9px] font-medium" style={{ color: "#22d3ee" }}>● live</span>
          ) : null}
          {headerRight}
        </div>
        {collapsible && (
          <div className="text-[var(--color-fg-dim)] ml-1">
            {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </div>
        )}
      </div>
      {/* Body */}
      {!collapsed && (
        <div className="px-3 py-2.5">
          {children}
        </div>
      )}
    </div>
  );
}

/** Small inline help icon — shows tooltip on hover. */
function HelpTip({ text }: { text: string }) {
  return (
    <Tooltip text={text} side="right">
      <span className="cursor-help select-none rounded-full border border-[var(--color-border-subtle)] px-1.5 py-px text-[9px] font-medium text-[var(--color-fg-dim)] hover:border-[color-mix(in_srgb,#2EA8FF_35%,transparent)] hover:text-[var(--color-fg-muted)]">
        ?
      </span>
    </Tooltip>
  );
}

/** One labeled number input knob. */
export function Knob({ label, value, min, max, step, unit, tip, disabled, onChange }: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  tip: string;
  disabled?: boolean;
  onChange: (v: number) => void;
}) {

  return (
    <div className="flex flex-col gap-2 group">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-semibold text-[var(--color-fg-dim)] uppercase tracking-wider">{label}</span>
          <HelpTip text={tip} />
        </div>
        <div className="flex items-center gap-1 bg-black/20 px-1.5 py-0.5 rounded border border-white/5 group-hover:border-white/10 transition-colors">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={value}
            disabled={disabled}
            onChange={(e) => { const n = Number(e.target.value); if (Number.isFinite(n)) onChange(n); }}
            className={
              "w-12 bg-transparent text-right font-mono text-[11px] font-bold tabular-nums focus:outline-none focus:ring-1 focus:ring-sky-500/50 rounded " +
              (disabled ? "cursor-not-allowed opacity-50" : "text-sky-400")
            }
          />
          {unit ? <span className="text-[10px] font-medium text-[var(--color-fg-dim)]">{unit}</span> : null}
        </div>
      </div>
      <div className="w-full pt-1">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(e) => { const n = Number(e.target.value); if (Number.isFinite(n)) onChange(n); }}
          className="w-full h-1.5 bg-black/40 rounded-lg cursor-pointer"
          style={{ accentColor: '#38bdf8' }}
        />
      </div>
    </div>
  );
}

export function SuggestLinesButton() {
  const { refreshSuggestedBounceZones, floorCandlesStatus, chartAnalytics, model } = useApp();
  const [noKeyWarning, setNoKeyWarning] = useState(false);

  const candlesLoading = floorCandlesStatus === "loading";
  const visionLoading = chartAnalytics.visionDetectStatus === "loading";
  const loading = candlesLoading || visionLoading;
  const disabled = floorCandlesStatus === "idle" || candlesLoading;
  const noApiKey = !model.apiKey.trim();

  // Auto-hide the "no key" warning after 3 s
  useEffect(() => {
    if (!noKeyWarning) return;
    const t = window.setTimeout(() => setNoKeyWarning(false), 3000);
    return () => window.clearTimeout(t);
  }, [noKeyWarning]);

  const tooltipText = noKeyWarning
    ? "⚠ Connect your LLM API key in Setup first."
    : candlesLoading
      ? "Fetching candles… bounce lines will appear automatically."
      : visionLoading
        ? "Running vision analysis… AI is reading the chart."
        : disabled
          ? "Paste a mint to load bounce lines automatically."
          : noApiKey
            ? "Connect your LLM API key in Setup to enable AI vision analysis."
            : chartAnalytics.visionDetectError
              ? `Last vision attempt failed: ${chartAnalytics.visionDetectError}`
              : chartAnalytics.visionDetectStatus === "done"
                ? "Re-run: algo detection + AI vision analysis of the chart."
                : "Re-run bounce detection + AI vision analysis of the chart.";

  const label = candlesLoading
    ? "Loading candles…"
    : visionLoading
      ? "AI reading chart…"
      : "Refresh bounce lines";

  const isReady = !disabled && !loading && !noApiKey && !chartAnalytics.visionDetectError;

  const handleClick = () => {
    if (disabled || loading) return;
    if (noApiKey) {
      setNoKeyWarning(true);
      return;
    }
    refreshSuggestedBounceZones();
  };

  return (
    <Tooltip text={tooltipText} side="left">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || loading}
        style={isReady ? {
          background: "linear-gradient(135deg, #0c4a6e 0%, #0369a1 45%, #0ea5e9 100%)",
          boxShadow: "0 0 10px rgba(14,165,233,0.45), 0 0 22px rgba(3,105,161,0.3)",
          border: "1px solid rgba(125,211,252,0.35)",
          animation: "untBetaPulse 2.4s ease-in-out infinite",
        } : noApiKey && !disabled ? {
          background: "rgba(220,38,38,0.08)",
          border: "1px solid rgba(248,113,113,0.25)",
        } : undefined}
        className={
          "relative flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[10px] font-bold tracking-wide transition-all " +
          (disabled || loading
            ? "cursor-not-allowed border-purple-400/15 bg-purple-500/5 text-purple-100/35"
            : noKeyWarning
              ? "border-rose-400/60 bg-rose-500/15 text-rose-200"
              : noApiKey
                ? "cursor-pointer border-rose-500/25 text-rose-300/70 hover:border-rose-400/40 hover:text-rose-200/90"
                : chartAnalytics.visionDetectError
                  ? "border-red-400/35 bg-red-500/10 text-red-200 hover:bg-red-500/20"
                  : "text-white hover:brightness-110")
        }
      >
        {/* BETA badge */}
        {isReady && (
          <span
            style={{ fontSize: "7.5px", letterSpacing: "0.1em", opacity: 0.9 }}
            className="rounded bg-white/15 px-1 py-px font-black uppercase text-white"
          >
            BETA
          </span>
        )}
        {noKeyWarning
          ? "⚠"
          : loading
            ? <span className="inline-block h-2.5 w-2.5 animate-spin rounded-full border border-purple-300/60 border-t-purple-200" />
            : chartAnalytics.visionDetectError
              ? "⚠"
              : null}
        {label}
      </button>
    </Tooltip>
  );
}

export function BounceZonesEditor({ mint }: { mint: string }) {
  const { bounceZones, toggleBounceZone, updateBounceZonePrice, addBounceZone, removeBounceZone } = useApp();
  const [newPrice, setNewPrice] = useState("");

  const zones = bounceZones.filter((z) => z.mint === mint).sort((a, b) => b.price - a.price);

  return (
    <div className="space-y-2">
      {zones.length === 0 && (
        <p className="text-[10px] leading-snug text-[var(--color-fg-dim)]">
          Bounce lines load automatically once 3 000 candles are fetched. Add your own levels below.
        </p>
      )}

      {zones.map((z) => (
        <ZoneRow key={z.id} zone={z}
          onToggle={() => toggleBounceZone(z.id)}
          onPriceChange={(p) => updateBounceZonePrice(z.id, p)}
          onRemove={() => removeBounceZone(z.id)}
        />
      ))}

      {/* Add manual zone */}
      <div className="flex gap-1.5">
        <input
          type="number"
          placeholder="Add price level…"
          value={newPrice}
          onChange={(e) => setNewPrice(e.target.value)}
          className="unt-input h-7 min-w-0 flex-1 font-mono text-[11px] tabular-nums"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const p = Number(newPrice);
              if (p > 0) { addBounceZone(mint, p); setNewPrice(""); }
            }
          }}
        />
        <button
          type="button"
          className="rounded-md border border-[var(--color-border-subtle)] px-2.5 py-1 text-[11px] text-[var(--color-fg-dim)] hover:border-sky-400/40 hover:text-sky-300"
          onClick={() => {
            const p = Number(newPrice);
            if (p > 0) { addBounceZone(mint, p); setNewPrice(""); }
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
}

function ZoneRow({ zone, onToggle, onPriceChange, onRemove }: {
  zone: UserBounceZone;
  onToggle: () => void;
  onPriceChange: (p: number) => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String((zone.price ?? 0).toFixed(2)));

  const commit = () => {
    const p = Number(draft);
    if (p > 0) onPriceChange(p);
    else setDraft((zone.price ?? 0).toFixed(2));
    setEditing(false);
  };

  return (
    <div className={
      "flex items-center gap-1.5 rounded-md border px-2 py-1 transition-colors " +
      (zone.enabled
        ? "border-sky-400/25 bg-sky-500/5"
        : "border-[var(--color-border-subtle)] opacity-50")
    }>
      {/* Enable toggle */}
      <button type="button" onClick={onToggle} className="shrink-0 text-[10px]" title={zone.enabled ? "Disable zone" : "Enable zone"}>
        <span className={zone.enabled ? "text-sky-400" : "text-[var(--color-fg-dim)]"}>
          {zone.enabled ? "●" : "○"}
        </span>
      </button>

      {/* Price */}
      {editing ? (
        <input
          autoFocus
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft((zone.price ?? 0).toFixed(2)); setEditing(false); } }}
          className="unt-input h-5 w-24 font-mono text-[11px] tabular-nums"
        />
      ) : (
        <button
          type="button"
          className="min-w-0 flex-1 text-left font-mono text-[11px] tabular-nums text-[var(--color-fg)] hover:text-sky-300"
          onClick={() => { setDraft((zone.price ?? 0).toFixed(2)); setEditing(true); }}
          title="Click to edit price"
        >
          ${(zone.price ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
        </button>
      )}

      {/* Meta — show which timeframes contributed, or "manual" */}
      <span className="shrink-0 text-[10px] text-[var(--color-fg-dim)]">
        {(() => {
          const src = (zone as { sources?: string }).sources;
          if (!src) return zone.touches > 0 ? `×${zone.touches}` : "manual";
          if (src === "swing") return "Swing low";
          if (src.startsWith("swing+")) return `Swing · ${src.slice("swing+".length)}`;
          if (src.startsWith("vision·")) return `👁 ${src.slice("vision·".length)}`;
          return src;
        })()}
      </span>

      {/* Remove */}
      <button type="button" onClick={onRemove} className="shrink-0 text-[10px] text-[var(--color-fg-dim)] hover:text-red-400" title="Remove zone">
        ✕
      </button>
    </div>
  );
}

export function ScalperPaperPanel({
  snapshot,
  mint,
  orderBookConn,
  paperSessionActive,
  tradingMode,
  liveChainTrades,
}: {
  snapshot: ScalperPaperSnapshot | null;
  mint: string | null;
  orderBookConn: "idle" | "connecting" | "open" | "closed" | "error";
  paperSessionActive: boolean;
  tradingMode: TradingMode;
  liveChainTrades: BotTradeRow[];
}) {
  const { scalperUserConfig, setScalperUserConfig } = useApp();

  const wrap = (belowRules: ReactNode) => (
    <div className="space-y-2 text-[12px]">
      {/* ── Entry card ── */}
      <SettingsCard
        label="Entry"
        tip="Rules for when we open a buy."
        liveSession={paperSessionActive}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
          <Knob label="Dip %" value={scalperUserConfig.dipMinPct} min={1} max={50} step={1} unit="%"
            tip="How far the chart needs to drop from its recent high before we look for a buy. After each trade, we start measuring again."
            onChange={(v) => setScalperUserConfig({ dipMinPct: v })} />
          <Knob label="Min buy" value={scalperUserConfig.catalystMinSol} min={0.01} max={10} step={0.05} unit="SOL"
            tip="We only jump in if someone buys this much SOL or more. Tiny buys are ignored so we don't chase noise."
            onChange={(v) => setScalperUserConfig({ catalystMinSol: v })} />
        </div>
      </SettingsCard>

      {/* ── Exit card ── */}
      <SettingsCard label="Exit" tip="Rules for when we sell and leave the trade." liveSession={paperSessionActive}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
          <Knob label="Take profit %" value={scalperUserConfig.takeProfitPct} min={1} max={1000} step={1} unit="%"
            tip="We sell for profit when the chart goes up this much above where we bought (same numbers as on your chart)."
            onChange={(v) => setScalperUserConfig({ takeProfitPct: v })} />
          <Knob label="Stop SOL" value={scalperUserConfig.minOrderBookSellSolForStop} min={0.01} max={10} step={0.05} unit="SOL"
            tip="If we see a sell this big or bigger, we treat it as a stop and get out. Smaller sells don't count."
            onChange={(v) => setScalperUserConfig({ minOrderBookSellSolForStop: v })} />
          <Knob label="Cooldown" value={Math.round(scalperUserConfig.reentryCooldownMs / 1000)} min={2} max={300} step={1} unit="s"
            tip="Seconds to wait after a closed trade before looking for a new entry. Default 30 s prevents back-to-back double entries."
            onChange={(v) => setScalperUserConfig({ reentryCooldownMs: Math.round(v) * 1000 })} />
        </div>
      </SettingsCard>

      {/* ── Execution card (real mode only) ── */}
      {tradingMode === "real" ? (
        <SettingsCard
          label="Execution"
          tip="Only used when real money is on. Slippage = how much the price can move before your trade still goes through. Priority fee = a little extra SOL so the chain handles your trade faster."
          liveSession={paperSessionActive}
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
              <Knob label="Slippage %" value={scalperUserConfig.realSlippagePct} min={1} max={50} step={1} unit="%"
                tip="How much wiggle room the price has. Higher usually means fewer failed trades on jumpy coins."
                onChange={(v) => setScalperUserConfig({ realSlippagePct: v })} />
              <Knob label="Priority fee" value={scalperUserConfig.realPriorityFeeSol} min={0.00001} max={0.1} step={0.0005} unit="SOL"
                tip="Extra SOL paid so your trade gets picked up sooner. Try around 0.001 SOL to start."
                onChange={(v) => setScalperUserConfig({ realPriorityFeeSol: v })} />
            </div>
        </SettingsCard>
      ) : null}

      {/* ── Bounce zones card ── */}
      {mint ? (
        <SettingsCard
          label="Bounce zones"
          tip="Lines on the chart where price bounced a few times. Turn a line on (solid dot) and we only buy when price is close to it. Drag the line on the chart to move it."
          accent="sky"
          headerRight={<SuggestLinesButton />}
          collapsible={true}
          defaultCollapsed={true}
        >
          <Tooltip
            text="Refresh bounce lines sends one chart image to your LLM per click — costs more than plain chat. Check your provider usage dashboard."
            side="bottom"
          >
            <div
              role="note"
              className="mb-2 flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/[0.07] px-2 py-1.5 text-[10px] font-medium leading-snug text-amber-100/95"
            >
              <span className="shrink-0 select-none" aria-hidden>
                ⚠
              </span>
              <span>Check API credit spend - still optimizing</span>
            </div>
          </Tooltip>
          <BounceZonesEditor mint={mint} />
        </SettingsCard>
      ) : null}

      {belowRules}
    </div>
  );

  if (!paperSessionActive) {
    return wrap(
      <p className="unt-help-text">
        Tap <strong className="font-semibold text-[var(--color-fg-muted)]">Start</strong> below. Paste a coin on the Chart tab so we can see live trades — numbers show up here when it&apos;s working.
      </p>,
    );
  }
  if (!mint) {
    return wrap(
      <p className="unt-help-text">
        Put this same coin on the Chart tab first — we need live trades to run.
      </p>,
    );
  }
  if (orderBookConn !== "open") {
    const plain =
      orderBookConn === "connecting"
        ? "Still connecting to live trades…"
        : orderBookConn === "error"
          ? "Something broke connecting to live trades. Refresh or try again."
          : "Live trades are turned off. Open the Chart tab with your coin.";
    return wrap(<p className="unt-help-text">{plain}</p>);
  }
  if (!snapshot) {
    return wrap(<p className="unt-help-text">Hang tight — connecting to live trades…</p>);
  }

  const st =
    snapshot.status === "watching"
      ? "Watching"
      : snapshot.status === "nearing"
        ? "Nearing bounce region — arming trade"
        : snapshot.status === "dip"
          ? "Dipped — not armed yet"
          : snapshot.status === "arming"
            ? `Armed — next ${scalperUserConfig.catalystMinSol}+ SOL buy fires`
            : "In a trade";

  const chainLegs =
    tradingMode === "real" ? liveChainTrades.filter(isBotTradeChain) : [];
  const chainClosed = chainLegs.length;
  const chainWins = chainLegs.filter((t) => t.netSol > 0).length;
  const chainWinRate = chainClosed > 0 ? (chainWins / chainClosed) * 100 : null;
  const chainNetSum = chainLegs.reduce((s, t) => s + t.netSol, 0);

  const tapePaperEstLegs: (BotTradeRowTape & { paperSolEstimate: NonNullable<BotTradeRowTape["paperSolEstimate"]> })[] =
    tradingMode === "paper"
      ? snapshot.botTrades.filter(
          (t): t is BotTradeRowTape & { paperSolEstimate: NonNullable<BotTradeRowTape["paperSolEstimate"]> } =>
            t.kind === "tape" && t.paperSolEstimate != null,
        )
      : [];
  const paperEstNetSum = tapePaperEstLegs.reduce((s, t) => s + t.paperSolEstimate.netSol, 0);

  return wrap(
    <>
      <div className="flex justify-between gap-2 text-[var(--color-fg-muted)]">
        <span>Status</span>
        <span
          className={
            snapshot.status === "nearing"
              ? "text-amber-400 font-medium"
              : snapshot.status === "arming"
                ? "text-emerald-400 font-medium"
                : snapshot.status === "in_trade"
                  ? "text-emerald-300 font-medium"
                  : "text-[var(--color-fg)]"
          }
        >
          {st}
        </span>
      </div>
      <div className="flex justify-between gap-2 text-[var(--color-fg-muted)]">
        <span>Win rate</span>
        <span className="text-[var(--color-fg)]">
          {tradingMode === "real"
            ? chainWinRate != null
              ? chainWinRate.toFixed(1) + "%"
              : "—"
            : snapshot.winRate != null
              ? snapshot.winRate.toFixed(1) + "%"
              : "—"}
        </span>
      </div>
      <div className="flex justify-between gap-2 text-[var(--color-fg-muted)]">
        <span title={tradingMode === "real" ? "SOL added or removed from your wallet after each finished trade." : "Adds up the chart move % on each practice trade."}>
          {tradingMode === "real" ? "Total SOL" : "Practice total %"}
        </span>
        <span
          className={
            tradingMode === "real"
              ? chainClosed > 0
                ? chainNetSum >= 0
                  ? "text-emerald-300"
                  : "text-red-400"
                : "text-[var(--color-fg)]"
              : snapshot.totalPnlPct >= 0
                ? "text-emerald-300"
                : "text-red-400"
          }
        >
          {tradingMode === "real"
            ? chainClosed > 0
              ? (chainNetSum >= 0 ? "+" : "") + formatSol(chainNetSum)
              : "—"
            : (((snapshot.totalPnlPct ?? 0) >= 0 ? "+" : "") + (snapshot.totalPnlPct ?? 0).toFixed(2) + "%")}
        </span>
      </div>
      {tradingMode === "paper" && snapshot.closedTrades > 0 ? (
        <div className="flex justify-between gap-2 text-[var(--color-fg-muted)]">
          <span title="Rough SOL profit on practice trades when we have enough price data (not perfect).">
            Est. practice SOL
          </span>
          <span
            className={
              tapePaperEstLegs.length > 0
                ? paperEstNetSum >= 0
                  ? "text-cyan-300/95"
                  : "text-orange-300/95"
                : "text-[var(--color-fg)]"
            }
          >
            {tapePaperEstLegs.length > 0
              ? (paperEstNetSum >= 0 ? "+" : "") + formatSol(paperEstNetSum)
              : "—"}
          </span>
        </div>
      ) : null}
      <div className="flex justify-between gap-2 text-[var(--color-fg-muted)]">
        <span>Closed trades</span>
        <span className="text-[var(--color-fg)]">
          {tradingMode === "real" ? chainClosed : snapshot.closedTrades}
        </span>
      </div>
      <div className="border-t border-[var(--color-border-subtle)] pt-2">
        <div className="unt-section-overline">Current trade</div>
      </div>
      {snapshot.currentTrade ? (
        <div className="space-y-1 text-[11px] leading-snug text-[var(--color-fg-muted)]">
          <div>Bought near {formatUsdCompact(snapshot.currentTrade.entryMcUsd)}</div>
          <div>Big buy that triggered us: {(snapshot.currentTrade.catalystSol ?? 0).toFixed(2)} SOL</div>
          {snapshot.currentTrade.lastMcUsd != null ? (
            <div>Chart now {formatUsdCompact(snapshot.currentTrade.lastMcUsd)}</div>
          ) : null}
          {snapshot.currentTrade.unrealizedPct != null ? (
            <div>
              Unrealized profit{" "}
              <span
                className={
                  snapshot.currentTrade.unrealizedPct >= 0 ? "text-emerald-300" : "text-red-400"
                }
              >
                {(snapshot.currentTrade.unrealizedPct >= 0 ? "+" : "") +
                  snapshot.currentTrade.unrealizedPct.toFixed(2) +
                  "%"}
              </span>
              {tradingMode === "real" ? (
                <span className="text-[var(--color-fg-dim)]"> (same as chart)</span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="text-[11px] text-[var(--color-fg-dim)]">
          {snapshot.status === "nearing" ? (
            <span className="text-amber-400 font-medium">⚠️ Nearing bounce region — arming trade</span>
          ) : snapshot.status === "arming" ? (
            <span className="text-emerald-400 font-medium">🟢 Armed — waiting for catalyst buy</span>
          ) : snapshot.status === "dip" ? (
            <span className="text-sky-400">Dipped — waiting for zone alignment</span>
          ) : (
            <span>Flat</span>
          )}
        </div>
      )}
    </>,
  );
}
